import { api } from './api';
import config from 'config';
import { ContinuationedResponse, PostType, PostTypeAsLoggedInUser } from 'types';
import { socialApi } from './socialApi';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { ModeratedFeedItemV2, PostFeedItem } from '@/features/posts/types';
import { getContinuationTokenInfiniteQueryOptions } from '@/utils/rtk';

const CONTENT_API = config.contentApi;

export const contentApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPublicPost: build.query<PostType, string>({
      async queryFn(creatorsContentId, _queryApi, _extraOptions, baseQuery) {
        const postRes = await baseQuery({
          url: `${CONTENT_API}/v1/sharing/creators-content/${creatorsContentId}`,
        });

        if (postRes.error) {
          return { error: postRes.error as FetchBaseQueryError };
        }

        const { data: postOverview, error: postOverviewError } = await _queryApi.dispatch(
          socialApi.endpoints.getPostOverview.initiate(creatorsContentId)
        );

        if (postOverviewError) {
          return { error: postOverviewError as FetchBaseQueryError };
        }

        return {
          data: {
            ...(postRes.data as {}),
            ...postOverview,
          } as PostType,
        };
      },
    }),
    getPostAsLoggedInUser: build.query<PostTypeAsLoggedInUser, string>({
      async queryFn(creatorsContentId, _queryApi, _extraOptions, baseQuery) {
        const postRes = await baseQuery({
          url: `${CONTENT_API}/v1/creators-content/${creatorsContentId}`,
          withAccessToken: true,
        });

        if (postRes.error) {
          return { error: postRes.error as FetchBaseQueryError };
        }

        const { data: postOverview, error: postOverviewError } = await _queryApi.dispatch(
          socialApi.endpoints.getPostOverviewAsLoggedInUser.initiate(creatorsContentId)
        );

        if (postOverviewError) {
          return { error: postOverviewError as FetchBaseQueryError };
        }

        return {
          data: {
            ...(postRes.data as {}),
            ...postOverview,
          } as PostTypeAsLoggedInUser,
        };
      },
    }),
    getLovemeterPosts: build.infiniteQuery<
      ContinuationedResponse<PostFeedItem>,
      { size: number; influencerId: string },
      Pick<ContinuationedResponse, 'continuationToken'>
    >({
      query: ({ queryArg: { size, influencerId }, pageParam: { continuationToken } }) => ({
        url: `${CONTENT_API}/v1/sharing/loved-public-posts/${influencerId}`,
        params: { size, continuationToken },
      }),
      transformResponse: (response: any) => {
        const updatedResponse = {
          ...response,
          continuationToken: response.continuationToken,
          data: response.posts,
        };
        delete updatedResponse.posts;
        return updatedResponse;
      },
      infiniteQueryOptions: getContinuationTokenInfiniteQueryOptions(),
    }),
    getLovemeterPostsAsLoggedInUser: build.infiniteQuery<
      ContinuationedResponse<PostFeedItem>,
      { size: number; influencerId: string },
      Pick<ContinuationedResponse, 'continuationToken'>
    >({
      query: ({ queryArg: { size, influencerId }, pageParam: { continuationToken } }) => ({
        url: `${CONTENT_API}/v1/feed/loved-public-posts/${influencerId}`,
        params: { size, continuationToken },
        withAccessToken: true,
      }),
      transformResponse: (response: any, _) => {
        const updatedResponse = {
          ...response,
          continuationToken: response.continuationToken,
          data: response.posts,
        };
        delete updatedResponse.posts;
        return updatedResponse;
      },
      infiniteQueryOptions: getContinuationTokenInfiniteQueryOptions(),
    }),
    getMyProfilePublicPosts: build.infiniteQuery<
      ContinuationedResponse<ModeratedFeedItemV2>,
      { size: number },
      Pick<ContinuationedResponse, 'continuationToken'>
    >({
      query: ({ queryArg: { size }, pageParam: { continuationToken } }) => ({
        url: `${CONTENT_API}/v2/feed/profile/public`,
        params: { size, continuationToken: continuationToken },
        withAccessToken: true,
      }),
      transformResponse: (response: any) => {
        const updatedResponse = {
          ...response,
          continuationToken: response.continuationToken,
          data: response.posts,
        };
        delete updatedResponse.posts;
        return updatedResponse;
      },
      infiniteQueryOptions: getContinuationTokenInfiniteQueryOptions(),

      providesTags: (result) =>
        result?.data
          ? [...result.data.map(({ id }) => ({ type: 'PublicPosts' as const, id }))]
          : [{ type: 'PublicPosts', id: 'PUBLIC_POSTS_LIST' }],
    }),
    getMyProfilePrivatePosts: build.infiniteQuery<
      ContinuationedResponse<ModeratedFeedItemV2>,
      { size: number },
      Pick<ContinuationedResponse, 'continuationToken'>
    >({
      query: ({ queryArg: { size }, pageParam: { continuationToken } }) => ({
        url: `${CONTENT_API}/v2/feed/profile/private`,
        params: { size, continuationToken },
        withAccessToken: true,
      }),
      transformResponse: (response: any) => {
        const updatedResponse = {
          ...response,
          continuationToken: response.continuationToken,
          data: response.posts,
        };
        delete updatedResponse.posts;
        return updatedResponse;
      },
      infiniteQueryOptions: getContinuationTokenInfiniteQueryOptions(),
      providesTags: (result) =>
        result?.data
          ? [...result.data.map(({ id }) => ({ type: 'PrivatePosts' as const, id }))]
          : [{ type: 'PrivatePosts', id: 'PRIVATE_POSTS_LIST' }],
    }),
    getProfilePublicPosts: build.infiniteQuery<
      ContinuationedResponse<PostFeedItem>,
      { size: number; username: string },
      Pick<ContinuationedResponse, 'continuationToken'>
    >({
      query: ({ queryArg: { size, username }, pageParam: { continuationToken } }) => ({
        url: `${CONTENT_API}/v1/sharing/feed/${username}/public`,
        params: { size, continuationToken },
      }),
      transformResponse: (response: any) => {
        const updatedResponse = {
          ...response,
          continuationToken: response.continuationToken,
          data: response.posts,
        };
        delete updatedResponse.posts;
        return updatedResponse;
      },
      infiniteQueryOptions: getContinuationTokenInfiniteQueryOptions(),
      providesTags: ['PublicPosts'],
    }),
    getProfilePrivatePosts: build.infiniteQuery<
      ContinuationedResponse<PostFeedItem>,
      { size: number; citizenId: string },
      Pick<ContinuationedResponse, 'continuationToken'>
    >({
      query: ({ queryArg: { size, citizenId }, pageParam: { continuationToken } }) => ({
        url: `${CONTENT_API}/v1/feed/${citizenId}/private`,
        params: { size, continuationToken },
        withAccessToken: true,
      }),
      transformResponse: (response: any) => {
        const updatedResponse = {
          ...response,
          continuationToken: response.continuationToken,
          data: response.posts,
        };
        delete updatedResponse.posts;
        return updatedResponse;
      },
      infiniteQueryOptions: getContinuationTokenInfiniteQueryOptions(),
      providesTags: ['PrivatePosts'],
    }),
  }),
});

export const {
  useGetPublicPostQuery,
  useGetPostAsLoggedInUserQuery,
  useGetMyProfilePrivatePostsInfiniteQuery,
  useGetMyProfilePublicPostsInfiniteQuery,
  useGetProfilePublicPostsInfiniteQuery,
  useGetProfilePrivatePostsInfiniteQuery,
  useGetLovemeterPostsInfiniteQuery,
  useGetLovemeterPostsAsLoggedInUserInfiniteQuery,
} = contentApi;
